import React, { useState, useMemo } from 'react';

// Helper to get the financial year string (e.g., "2024-2025") from a date
const getFinancialYear = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return month >= 3 ? `${year}-${year + 1}` : `${year - 1}-${year}`;
};

// --- Main Leave Statement Component ---
export default function LeaveStatement({ allRequests, userProfile }) {
    // Default the expanded year to the current financial year for better UX
    const [expandedYear, setExpandedYear] = useState(getFinancialYear(new Date()));

    const statementData = useMemo(() => {
        if (!userProfile?.dateOfJoining) return [];

        const approvedRequests = allRequests.filter(req => req.status === 'Approved');
        const requestsByYear = approvedRequests.reduce((acc, req) => {
            const year = getFinancialYear(req.startDate.toDate());
            if (!acc[year]) acc[year] = [];
            acc[year].push(req);
            return acc;
        }, {});

        const currentFinancialYear = getFinancialYear(new Date());
        const nextFinancialYear = getFinancialYear(new Date(new Date().setFullYear(new Date().getFullYear() + 1)));

        // Ensure the current financial year is always present, even if there are no requests
        if (!requestsByYear[currentFinancialYear]) {
            requestsByYear[currentFinancialYear] = [];
        }
        // --- UPDATED: Ensure the next financial year is always present for planning ---
        if (!requestsByYear[nextFinancialYear]) {
            requestsByYear[nextFinancialYear] = [];
        }

        let openingBalances = { vacation: 0, casual: 0, sick: 0 };
        const dateOfJoining = userProfile.dateOfJoining.toDate();

        const calculateProratedMonths = (joiningDate, fyStr) => {
            const fyEndYear = parseInt(fyStr.split('-')[1]);
            const fyEndDate = new Date(fyEndYear, 2, 31); // March 31st

            let monthsInFY = (fyEndDate.getFullYear() - joiningDate.getFullYear()) * 12;
            monthsInFY -= joiningDate.getMonth();
            monthsInFY += fyEndDate.getMonth();
            monthsInFY += 1; // Include the starting month

            // Exclude joining month if joined after the 1st
            if (joiningDate.getDate() > 1) {
                monthsInFY -= 1;
            }
            return Math.max(0, monthsInFY); // Ensure it's not negative
        };

        return Object.keys(requestsByYear).sort().map(yearStr => {
            const yearRequests = requestsByYear[yearStr];
            const financialYearStart = new Date(parseInt(yearStr.split('-')[0]), 3, 1);

            let credits = { vacation: 18, casual: 6, sick: 6 };

            if (getFinancialYear(dateOfJoining) === yearStr) {
                const months = calculateProratedMonths(dateOfJoining, yearStr);
                credits = { vacation: 1.5 * months, casual: 0.5 * months, sick: 0.5 * months };
            }

            const yearsOfService = (financialYearStart - dateOfJoining) / (1000 * 60 * 60 * 24 * 365.25);
            if (yearsOfService >= 5) {
                credits.vacation += 5;
            }

            const initialMonthlyAcc = { vacation: 0, casual: 0, sick: 0, special: 0 };
            const takenByMonth = yearRequests.reduce((acc, req) => {
                const month = req.startDate.toDate().toLocaleString('default', { month: 'long' });
                if (!acc[month]) acc[month] = { ...initialMonthlyAcc };

                const type = req.leaveType.toLowerCase();
                if (type.includes('vacation')) acc[month].vacation += req.duration;
                else if (type.includes('casual')) acc[month].casual += req.duration;
                else if (type.includes('sick')) acc[month].sick += req.duration;
                else if (['bereavement', 'maternity', 'paternity'].some(s => type.includes(s))) {
                    acc[month].special += req.duration;
                }
                return acc;
            }, {});

            const totalTaken = Object.values(takenByMonth).reduce((acc, monthData) => ({
                vacation: acc.vacation + monthData.vacation,
                casual: acc.casual + monthData.casual,
                sick: acc.sick + monthData.sick,
                special: acc.special + monthData.special,
            }), { ...initialMonthlyAcc });


            const closing = {
                vacation: openingBalances.vacation + credits.vacation - totalTaken.vacation,
                casual: credits.casual - totalTaken.casual,
                sick: credits.sick - totalTaken.sick,
                special: totalTaken.special
            };

            const yearData = { year: yearStr, opening: { ...openingBalances }, credits, takenByMonth, closing };
            // Set the opening balance for the *next* year, carrying over vacation leave
            openingBalances.vacation = Math.min(closing.vacation, 45);
            return yearData;

        }).reverse();

    }, [allRequests, userProfile]);

    if (!userProfile?.dateOfJoining) {
        return (
            <div className="text-center text-yellow-700 bg-yellow-50 p-4 rounded-md">
                <p>Your "Date of Joining" has not been set. Please contact HR to update your profile.</p>
            </div>
        );
    }

    if (statementData.length === 0) {
        return <div className="text-center text-gray-500 py-8"><p>No leave history found.</p></div>;
    }

    const financialYearMonths = ["April", "May", "June", "July", "August", "September", "October", "November", "December", "January", "February", "March"];

    return (
        <div className="space-y-4">
            {statementData.map(data => (
                <div key={data.year} className="border border-gray-200 rounded-lg">
                    <div
                        className="p-4 bg-gray-50 cursor-pointer hover:bg-gray-100 flex justify-between items-center"
                        onClick={() => setExpandedYear(expandedYear === data.year ? null : data.year)}
                    >
                        <h3 className="text-lg font-semibold text-[#213368]">Financial Year: {data.year}</h3>
                        <span className="text-sm font-medium text-gray-500">
                            {expandedYear === data.year ? 'Collapse' : 'Expand'}
                        </span>
                    </div>
                    {expandedYear === data.year && (
                        <div className="overflow-x-auto p-4">
                            <table className="min-w-full text-sm">
                                <thead className="bg-gray-100">
                                    <tr>
                                        <th className="px-4 py-2 text-left font-medium">Leave Category</th>
                                        <th className="px-4 py-2 text-center font-medium">Vacation</th>
                                        <th className="px-4 py-2 text-center font-medium">Casual</th>
                                        <th className="px-4 py-2 text-center font-medium">Sick</th>
                                        <th className="px-4 py-2 text-center font-medium">Special Leaves</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className="bg-gray-50 font-semibold">
                                        <td className="px-4 py-2">Opening Balance</td>
                                        <td className="px-4 py-2 text-center">{data.opening.vacation.toFixed(1)}</td>
                                        <td className="px-4 py-2 text-center">{data.opening.casual.toFixed(1)}</td>
                                        <td className="px-4 py-2 text-center">{data.opening.sick.toFixed(1)}</td>
                                        <td className="px-4 py-2 text-center text-gray-400">N/A</td>
                                    </tr>
                                    <tr className="bg-green-50">
                                        <td className="px-4 py-2 font-semibold">Credited</td>
                                        <td className="px-4 py-2 text-center text-green-700 font-bold">+{data.credits.vacation.toFixed(1)}</td>
                                        <td className="px-4 py-2 text-center text-green-700 font-bold">+{data.credits.casual.toFixed(1)}</td>
                                        <td className="px-4 py-2 text-center text-green-700 font-bold">+{data.credits.sick.toFixed(1)}</td>
                                        <td className="px-4 py-2 text-center text-gray-400">N/A</td>
                                    </tr>
                                    {financialYearMonths.map(month => (
                                        <tr key={month} className="border-t">
                                            <td className="px-4 py-2">{month}</td>
                                            <td className="px-4 py-2 text-center text-red-600">{data.takenByMonth[month]?.vacation || 0}</td>
                                            <td className="px-4 py-2 text-center text-red-600">{data.takenByMonth[month]?.casual || 0}</td>
                                            <td className="px-4 py-2 text-center text-red-600">{data.takenByMonth[month]?.sick || 0}</td>
                                            <td className="px-4 py-2 text-center text-red-600">{data.takenByMonth[month]?.special || 0}</td>
                                        </tr>
                                    ))}
                                    <tr className="bg-gray-200 font-bold text-gray-800">
                                        <td className="px-4 py-3">Closing Balance / Total Taken</td>
                                        <td className="px-4 py-3 text-center">{data.closing.vacation.toFixed(1)}</td>
                                        <td className="px-4 py-3 text-center">{data.closing.casual.toFixed(1)}</td>
                                        <td className="px-4 py-3 text-center">{data.closing.sick.toFixed(1)}</td>
                                        <td className="px-4 py-3 text-center">{data.closing.special}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

